import type { ActionsUnion } from '@rxsv/core';
import { U, createReducer } from '@rxsv/core';
import type { ExtendedFetch } from '@bayer-int/portletce-shared';
import { findLast } from 'lodash-es';

import type { ActionTransition, PerformanceCategorySummary, PerformanceStore } from 'header/types';
import { defaults } from 'portlet/models/config';

import { Action as InitAction } from '../init';
import { Action as ViewAction } from '../view';
import type { AppSelector } from '../selectors';
import { Action as FiltersSetting } from '../filters-settings';

export type ActionType =
    | typeof InitAction.PORTLET_INIT.type
    | typeof InitAction.PORTLET_INIT_SUCCESS.type
    | typeof ViewAction.RENDER_VIEW.type
    | typeof ViewAction.VIEW_DATA_LOADED.type
    | typeof InitAction.CHART_RENDERED.type
    | typeof ViewAction.RELOAD_DATA.type;

export interface PerformanceState {
    currentAction: ActionType | null;
    currentActionTime: number;
    waitingForAction: ActionType[];
    latestTrace: {
        from: ActionType | null;
        to: ActionType;
        deltaTime: number;
        description: string;
    } | null;
}

const performanceCategorySummary: PerformanceCategorySummary[] = [
    {
        threshold: defaults.performancethreshold as number,
        description:
            'Initialization took longer than expected. This may be due to temporary issues with hardware which is currently used or with some internal errors.\nIf the issue persists, please inform viz-it team about these difficulties.',
        time: 0,
        title: 'Preparation',
        key: 'prep',
    },
    {
        threshold: defaults.performancethreshold as number,
        description:
            'The data call took longer than expected. This may be due to temporary constraints in the data source system or network slowdown / bandwidth issues.\nIf the issue persists, please inform the owner of the report / data source owner.',
        time: 0,
        title: 'Data calls',
        key: 'data',
    },
    {
        threshold: defaults.performancethreshold as number,
        description:
            'The visualization took longer than expected. This may be due to the large amount of data being visualized or a complex visualization – or a temporary resource constraint on the local PC.\nIf the issue persists, please inform the owner of the report.',
        time: 0,
        title: 'Visualization',
        key: 'vis',
    },
];

export const initialState: PerformanceStore = {
    transitions: [],
    actionsTransitions: {
        [InitAction.PORTLET_INIT.type]: [InitAction.PORTLET_INIT_SUCCESS.type],
        [InitAction.PORTLET_INIT_SUCCESS.type]: [ViewAction.RENDER_VIEW.type],
        [ViewAction.RENDER_VIEW.type]: [ViewAction.VIEW_DATA_LOADED.type],
        [ViewAction.VIEW_DATA_LOADED.type]: [
            InitAction.CHART_RENDERED.type,
            ViewAction.RENDER_VIEW.type,
        ],
        [InitAction.CHART_RENDERED.type]: [
            InitAction.PORTLET_INIT.type,
            ViewAction.RELOAD_DATA.type,
            ViewAction.RENDER_VIEW.type,
        ],
        [ViewAction.RELOAD_DATA.type]: [
            InitAction.CHART_RENDERED.type,
            ViewAction.RENDER_VIEW.type,
        ],
    },
    performanceCategorySummary: performanceCategorySummary,
    totalDataCallTime: 0,
    filtersDelayTimestamp: 0,
    filtersDelayDeltaTime: 0,
};

export const descriptions: Record<ActionType, string> = {
    [InitAction.PORTLET_INIT.type]: 'successful initialization',
    [InitAction.PORTLET_INIT_SUCCESS.type]: 'rendering view',
    [ViewAction.RENDER_VIEW.type]: 'successful load of the data',
    [ViewAction.VIEW_DATA_LOADED.type]: 'rendering chart',
    [InitAction.CHART_RENDERED.type]: 'start of initialization',
    [ViewAction.RELOAD_DATA.type]: '',
};

export const Action = U.createUnion(
    U.caseOf('RESET_PERFORMANCE_TRACES')(),
    U.caseOf('RESET_PERFORMANCE_DATA_CALLS_TIME')(),
    U.caseOf('SAVE_PERFORMANCE_TRACE')<{ transition: ActionTransition; threshold?: number }>(),
    U.caseOf('SAVE_PERFORMANCE_DATA_CALLS_TIME')<number>(),
    U.caseOf('SAVE_FILTERS_DELAY_TIMESTAMP')<number>(),
    U.caseOf('SAVE_FILTERS_DELAY_DELTA_TIME')<number>(),
);

export type Action = ActionsUnion<typeof Action>;

export const reducer = createReducer(initialState)<Action>({
    RESET_PERFORMANCE_TRACES: () => initialState,
    RESET_PERFORMANCE_DATA_CALLS_TIME: state => ({ ...state, totalDataCallTime: 0 }),
    SAVE_PERFORMANCE_TRACE: (state, { payload }) => {
        const newTransitions = state.transitions
            ? mergeDuplicateTransitions([...state.transitions, payload.transition])
            : [payload.transition];

        return newTransitions.some(({ from }) => from === ViewAction.VIEW_DATA_LOADED.type)
            ? {
                  ...state,
                  transitions: newTransitions,
                  performanceCategorySummary: summarizePerformanceCategories(
                      newTransitions,
                      state,
                      payload?.threshold,
                  ),
              }
            : { ...state, transitions: newTransitions };
    },
    SAVE_PERFORMANCE_DATA_CALLS_TIME: (state, { payload }) => ({
        ...state,
        totalDataCallTime: payload,
    }),
    SAVE_FILTERS_DELAY_TIMESTAMP: (state, { payload }) => ({
        ...state,
        filtersDelayTimestamp: payload,
    }),
    SAVE_FILTERS_DELAY_DELTA_TIME: (state, { payload }) => ({
        ...state,
        filtersDelayDeltaTime: payload,
    }),
});

const transitionsSelector: AppSelector<ActionTransition[] | null> = ({ performance }) =>
    performance.transitions;
const performanceStateSelector: AppSelector<PerformanceStore> = state => state.performance;
const isPerformanceLowSelector: AppSelector<boolean> = ({ performance }) =>
    performance.performanceCategorySummary.some(({ time, threshold }) => time > threshold) &&
    performance.totalDataCallTime !== 0;

export const Selectors = {
    performanceStateSelector,
    transitionsSelector,
    isPerformanceLowSelector,
};

export const recordPerformanceDuration =
    (fetchFunc: ExtendedFetch, dispatchFunc?: (duration: number) => void): ExtendedFetch =>
    async (url, init = {}, opts = {}) => {
        const startTime = Date.now();
        let duration = 0;
        try {
            const response = await fetchFunc(url, init, opts);
            duration = Date.now() - startTime;
            return response;
        } catch (error) {
            duration = Date.now() - startTime;
            throw error;
        } finally {
            if (dispatchFunc) dispatchFunc(duration);
        }
    };

function mergeDuplicateTransitions(transitions: ActionTransition[]): ActionTransition[] {
    const uniqueTransitions: Record<string, ActionTransition> = {};

    // Iterate through the array in reverse to ensure the last occurrence is kept
    for (let i = transitions.length - 1; i >= 0; i--) {
        const transition = transitions[i];
        if (!transition) continue;
        const key = `${transition.from}_${transition.to}`;
        if (!uniqueTransitions[key]) {
            uniqueTransitions[key] = transition;
        }
    }

    // Reverse the result to maintain the original order
    return Object.values(uniqueTransitions).reverse();
}

function summarizePerformanceCategories(
    transitions: ActionTransition[],
    state: PerformanceStore,
    threshold: number = defaults.performancethreshold as number,
): PerformanceCategorySummary[] {
    return state.performanceCategorySummary.map(event => {
        if (event.key == 'prep') {
            return {
                ...event,
                time:
                    state.filtersDelayDeltaTime > 0
                        ? transitions.reduce((acc, cur) => {
                              if (
                                  cur.from === InitAction.PORTLET_INIT.type ||
                                  cur.from === InitAction.PORTLET_INIT_SUCCESS.type ||
                                  cur.from === ViewAction.RENDER_VIEW.type
                              ) {
                                  return acc + cur.deltaTime;
                              } else return acc;
                          }, 0) - state.filtersDelayDeltaTime
                        : transitions.reduce((acc, cur) => {
                              if (
                                  cur.from === InitAction.PORTLET_INIT.type ||
                                  cur.from === InitAction.PORTLET_INIT_SUCCESS.type ||
                                  cur.from === ViewAction.RENDER_VIEW.type
                              ) {
                                  return acc + cur.deltaTime;
                              } else return acc;
                          }, 0),
                threshold,
            };
        }
        if (event.key == 'data') {
            return {
                ...event,
                time: state.totalDataCallTime,
                threshold,
            };
        }
        if (event.key == 'vis') {
            const toDataLoaded =
                findLast(
                    transitions,
                    ({ from, to }) =>
                        from === ViewAction.RENDER_VIEW.type &&
                        to === ViewAction.VIEW_DATA_LOADED.type,
                )?.deltaTime || 0;

            const toRenderView =
                findLast(
                    transitions,
                    ({ from, to }) =>
                        from === ViewAction.VIEW_DATA_LOADED.type &&
                        to === ViewAction.RENDER_VIEW.type,
                )?.deltaTime || 0;

            // Portlets are prepared after choosing Filter ( porlet-selector ) in filterDrawer, but is rendered after applying it, so it creates difference in time between events, that is why we need to distinct two different times.
            const timeWhenFiltersWereApplied =
                toDataLoaded + toRenderView - state.filtersDelayDeltaTime;

            const timeWithoutFilters = toDataLoaded + toRenderView;

            // We can assume that reading data from cache is under 100ms
            const time =
                state.filtersDelayDeltaTime > 0 ? timeWhenFiltersWereApplied : timeWithoutFilters;

            return {
                ...event,
                time,
                threshold,
            };
        } else return event;
    });
}
